/** Must match server `ADMIN_FEATURE_KEYS` / login `permissions` payload. */
export const ADMIN_FEATURE_KEYS = [
  'opportunities',
  'investors',
  'inquiries',
  'businessInquiries',
  'orders',
  'investments',
  'withdrawals',
  'unitPrices',
  'interestPayouts',
  'dividendPayouts',
  'blogs',
  'webinars',
  'campaigns',
  'comingSoon',
  'newsletter',
  'documents',
  'faqs',
  'kyc',
  'investorOpportunityTerms',
  'secondaryMarket',
  'crm',
  'dashboard',
  'adminTeam',
] as const

export type AdminFeatureKey = typeof ADMIN_FEATURE_KEYS[number]

export type FeatureAccessLevel = 'none' | 'read' | 'write'

const rank: Record<FeatureAccessLevel, number> = {
  none: 0,
  read: 1,
  write: 2,
}

export const accessMeetsRequired = (
  effective: string | undefined,
  required: 'read' | 'write',
): boolean => {
  const e = (effective || 'none') as FeatureAccessLevel
  return (rank[e] ?? 0) >= rank[required]
}

const allWrite = (): Record<AdminFeatureKey, FeatureAccessLevel> => {
  const o = {} as Record<AdminFeatureKey, FeatureAccessLevel>
  for (const k of ADMIN_FEATURE_KEYS) {
    o[k] = 'write'
  }
  return o
}

const credibilaAdminMatrix = (): Record<AdminFeatureKey, FeatureAccessLevel> => {
  const o = allWrite()
  o.adminTeam = 'none'
  return o
}

const credibilaDevMatrix = (): Record<AdminFeatureKey, FeatureAccessLevel> => {
  const o = {} as Record<AdminFeatureKey, FeatureAccessLevel>
  for (const k of ADMIN_FEATURE_KEYS) {
    o[k] = 'none'
  }
  o.crm = 'write'
  return o
}

/** Fallback when API omits `permissions` (older servers). */
export const getDefaultPermissionsForRole = (
  role: string,
): Record<AdminFeatureKey, FeatureAccessLevel> => {
  if (role === 'CREDIBILA_DEV') {
    return credibilaDevMatrix()
  }
  if (role === 'SUPERUSER') {
    return allWrite()
  }
  if (role === 'CREDIBILA_ADMIN') {
    return credibilaAdminMatrix()
  }
  return credibilaAdminMatrix()
}

export const normalizePermissionsFromLogin = (
  role: string,
  payload?: Partial<Record<AdminFeatureKey, FeatureAccessLevel>> | null,
): Record<AdminFeatureKey, FeatureAccessLevel> => {
  const defaults = getDefaultPermissionsForRole(role)
  if (!payload) {
    return defaults
  }
  const merged = { ...defaults }
  for (const k of ADMIN_FEATURE_KEYS) {
    const v = payload[k]
    if (v === 'none' || v === 'read' || v === 'write') {
      merged[k] = v
    }
  }
  return merged
}

/** Full access matrix for tests and dev defaults. */
export const FULL_ADMIN_PERMISSIONS = normalizePermissionsFromLogin('SUPERUSER', null)
